import { useEffect, useState } from 'react';
import { HardDrive, RefreshCw, CheckCircle2, XCircle, CloudOff, Cloud } from 'lucide-react';
import { syncApi } from '../api/endpoints';
import { Spinner } from '../components/ui/Spinner';
import { Alert } from '../components/ui/Alert';

export function Sync() {
  const [status, setStatus] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [result, setResult] = useState(null);
  const [lastSync, setLastSync] = useState(localStorage.getItem('vvsrs.lastSync') || null);

  const checkStatus = async () => {
    try {
      const s = await syncApi.status();
      setStatus(s);
    } catch (e) {
      setStatus({ online: false, error: e.message });
    }
  };

  useEffect(() => { checkStatus(); }, []);

  const handlePull = async () => {
    setPulling(true);
    setResult(null);
    try {
      const snap = await syncApi.pull(lastSync, null);
      localStorage.setItem('vvsrs.lastSync', snap.serverTimeUtc);
      setLastSync(snap.serverTimeUtc);
      setResult({
        ok: true,
        counts: {
          clientes: snap.clientes?.length || 0,
          empleados: snap.empleados?.length || 0,
          servicios: snap.servicios?.length || 0,
          refacciones: snap.refacciones?.length || 0,
          relaciones: snap.relaciones?.length || 0,
          items: snap.items?.length || 0,
          facturas: snap.facturas?.length || 0,
        },
      });
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.error || e.message });
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 max-w-3xl">
      <header className="flex items-center gap-3">
        <HardDrive className="text-cyan-400" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-white">Sincronización</h1>
          <p className="text-sm text-muted">Móvil ↔ Servidor</p>
        </div>
      </header>

      <div className="bg-panel border border-border rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-white">Estado del servidor</h2>
          <button onClick={checkStatus} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
            <RefreshCw size={12} /> Refrescar
          </button>
        </div>
        {status === null ? <Spinner size={20} /> : (
          <div className="flex items-center gap-3">
            {status.online ? <Cloud className="text-emerald-400" size={20} /> : <CloudOff className="text-rose-400" size={20} />}
            <div>
              <p className="text-sm font-semibold text-white">{status.online ? 'Conectado' : 'Sin conexión'}</p>
              <p className="text-xs text-muted">
                {status.online
                  ? `${status.service} · ${new Date(status.serverTime).toLocaleString('es-MX')}`
                  : (status.error || 'No se pudo contactar el servidor')}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-panel border border-border rounded-xl p-5 space-y-3">
        <h2 className="font-semibold text-white">Descargar cambios (Pull)</h2>
        <p className="text-sm text-muted">
          Última sincronización: {lastSync ? new Date(lastSync).toLocaleString('es-MX') : <em>Nunca</em>}
        </p>
        <button onClick={handlePull} disabled={pulling} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-md disabled:opacity-50">
          {pulling ? <Spinner size={14} /> : <RefreshCw size={14} />}
          {pulling ? 'Sincronizando...' : 'Sincronizar ahora'}
        </button>

        {result && (
          result.ok ? (
            <Alert variant="success">
              <div className="font-semibold mb-1">Sincronización exitosa</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <span>Clientes: <strong>{result.counts.clientes}</strong></span>
                <span>Empleados: <strong>{result.counts.empleados}</strong></span>
                <span>Servicios: <strong>{result.counts.servicios}</strong></span>
                <span>Refacciones: <strong>{result.counts.refacciones}</strong></span>
                <span>Relaciones: <strong>{result.counts.relaciones}</strong></span>
                <span>Items: <strong>{result.counts.items}</strong></span>
                <span>Facturas: <strong>{result.counts.facturas}</strong></span>
              </div>
            </Alert>
          ) : (
            <Alert variant="error">{result.error}</Alert>
          )
        )}
      </div>
    </div>
  );
}
